import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, PieChart, ArrowUpRight, ArrowDownRight, Plus, X, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

export default function Finanzas() {
  const { token } = useAuth();
  const [showNewMovimientoModal, setShowNewMovimientoModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    tipo: 'ingreso',
    concepto: '',
    monto: '',
    socio: '',
    observaciones: ''
  });

  const [dashboardData, setDashboardData] = useState({
    balanceGeneral: { activos: 0, pasivos: 0, patrimonio: 0, crecimiento: 0 },
    cuotasPorMes: [],
    ultimosMovimientos: [],
    ingresosTotales: 0,
    gastosTotales: 0,
    beneficioNeto: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/finanzas/dashboard`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (err) {
      console.error("Error fetching finanzas dashboard data", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'Gs. 0';
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Nuevo movimiento:', formData);
    setShowNewMovimientoModal(false);
    setFormData({
      tipo: 'ingreso',
      concepto: '',
      monto: '',
      socio: '',
      observaciones: ''
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>;
  }

  const { balanceGeneral, cuotasPorMes, ultimosMovimientos, ingresosTotales, gastosTotales, beneficioNeto } = dashboardData;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Finanzas</h1>
        <button 
          onClick={() => setShowNewMovimientoModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <DollarSign className="w-5 h-5" />
          Nuevo Movimiento
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Activos Totales</p>
              <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(balanceGeneral?.activos)}</h3>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="flex items-center text-sm">
            <ArrowUpRight className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-500 font-medium">+{balanceGeneral?.crecimiento}%</span>
            <span className="text-gray-500 ml-1">vs mes anterior</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Pasivos</p>
              <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(balanceGeneral?.pasivos)}</h3>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="flex items-center text-sm">
            <ArrowDownRight className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-red-500 font-medium">N/A</span>
            <span className="text-gray-500 ml-1">vs mes anterior</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Patrimonio Neto</p>
              <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(balanceGeneral?.patrimonio)}</h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <PieChart className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="flex items-center text-sm">
            <ArrowUpRight className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-500 font-medium">N/A</span>
            <span className="text-gray-500 ml-1">vs mes anterior</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Estado de Cuotas</h2>
          <div className="space-y-4">
            {cuotasPorMes.map((mes, index) => (
              <div key={index} className="border-b pb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">{mes.mes}</span>
                  <span className="text-gray-600">{formatCurrency(mes.monto)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                    <span>Pagadas: {mes.pagadas}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <span>Pendientes: {mes.pendientes}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Últimos Movimientos</h2>
          <div className="space-y-4">
            {ultimosMovimientos.map((movimiento) => (
              <div key={movimiento.id} className="flex items-center justify-between border-b pb-4">
                <div>
                  <p className="font-medium text-gray-900">{movimiento.concepto}</p>
                  <p className="text-sm text-gray-500">{movimiento.fecha} - {movimiento.socio}</p>
                </div>
                <div className={`flex items-center ${
                  movimiento.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {movimiento.tipo === 'ingreso' ? '+' : '-'}
                  {formatCurrency(movimiento.monto)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Balance General</h2>
          <select className="border rounded-lg px-3 py-2 text-gray-700">
            <option>Último Mes</option>
            <option>Último Trimestre</option>
            <option>Último Año</option>
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="p-4 rounded-lg bg-gray-50">
            <p className="text-gray-500 mb-1">Ingresos Totales</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(ingresosTotales)}</p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <p className="text-gray-500 mb-1">Gastos Totales</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(gastosTotales)}</p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50">
            <p className="text-gray-500 mb-1">Beneficio Neto</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(beneficioNeto)}</p>
          </div>
        </div>
      </div>

      {showNewMovimientoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Nuevo Movimiento</h2>
              <button
                onClick={() => setShowNewMovimientoModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Movimiento
                </label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="ingreso">Ingreso</option>
                  <option value="egreso">Egreso</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Concepto
                </label>
                <input
                  type="text"
                  name="concepto"
                  value={formData.concepto}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: Pago de cuotas, compra de insumos, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto (₲)
                </label>
                <input
                  type="number"
                  name="monto"
                  value={formData.monto}
                  onChange={handleInputChange}
                  required
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Socio/Proveedor/Cliente
                </label>
                <input
                  type="text"
                  name="socio"
                  value={formData.socio}
                  onChange={handleInputChange}
                  required
                  placeholder="Nombre del socio, proveedor o cliente"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observaciones
                </label>
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Información adicional (opcional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div className="flex justify-end gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => setShowNewMovimientoModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Registrar Movimiento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
